import { useCallback, useEffect, useRef, useState } from 'react';
import type { TileMatchPlayState, TileMatchFinishAudit } from '../services/api';
import { decryptTileMatchBootstrapCipher } from '../utils/tileMatchBootstrapDecrypt';
import {
  loadTileMatchAssemblyExports,
  type TileMatchWasmExports,
} from '../utils/tileMatchWasmExports';

const WASM_BASE =
  (process.env.VITE_TILE_MATCH_WASM_BASE ?? '/wasm/tile-match').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<TileMatchWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('Tile Match WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadTileMatchAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useTileMatchWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<TileMatchWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError('Tile Match bootstrap is missing.');
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptTileMatchBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize Tile Match engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Tile Match WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const tryPickTile = useCallback(
    async (tileId: string): Promise<TileMatchPlayState> => {
      const wasm = exportsRef.current;
      if (!wasm) {
        throw new Error('Engine not ready.');
      }
      const json = wasm.tryPickTile(tileId);
      return JSON.parse(json) as TileMatchPlayState;
    },
    []
  );

  const applyPowerUp = useCallback(
    async (powerUpType: 'shuffle' | 'undo' | 'evacuate'): Promise<TileMatchPlayState> => {
      const wasm = exportsRef.current;
      if (!wasm) {
        throw new Error('Engine not ready.');
      }
      const json = wasm.usePowerUp(powerUpType);
      return JSON.parse(json) as TileMatchPlayState;
    },
    []
  );

  const getPlayState = useCallback(
    async (): Promise<TileMatchPlayState> => {
      const wasm = exportsRef.current;
      if (!wasm) {
        throw new Error('Engine not ready.');
      }
      const json = wasm.getPlayState();
      return JSON.parse(json) as TileMatchPlayState;
    },
    []
  );

  const buildFinishPayload = useCallback((): TileMatchFinishAudit | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as TileMatchFinishAudit;
  }, []);

  const disposeSession = useCallback(() => {
    exportsRef.current?.disposeSession();
    exportsRef.current = null;
    setReady(false);
  }, []);

  return {
    ready,
    error,
    tryPickTile,
    applyPowerUp,
    getPlayState,
    buildFinishPayload,
    disposeSession,
  };
}
